// src/components/Places.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInstantListCache } from "../hooks/useInstantListCache";
import { getSocket } from "../lib/socket";
import { useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import {
  FaSearch,
  FaPlus,
  FaSyncAlt,
  FaTrashAlt,
  FaFilter,
  FaGlobeEurope,
} from "react-icons/fa";

import Select from "react-select";
import {
  LuPhone,
  LuBuilding2,
  LuMapPin,
  LuTags,
} from "react-icons/lu";
import "../styles/Places.css";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 20;
const CACHE_KEY = "places:list:v1";

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getCountryCode = (country) => {
  const value = normalizeText(country);

  const countryCodes = {
    portugal: "pt",
    pt: "pt",

    france: "fr",
    franca: "fr",
    fr: "fr",

    spain: "es",
    espanha: "es",
    es: "es",

    germany: "de",
    alemanha: "de",
    de: "de",

    italy: "it",
    italia: "it",
    it: "it",

    belgium: "be",
    belgica: "be",
    be: "be",

    netherlands: "nl",
    holanda: "nl",
    "paises baixos": "nl",
    paisesbaixos: "nl",
    nl: "nl",

    switzerland: "ch",
    suica: "ch",
    ch: "ch",

    austria: "at",
    at: "at",

    poland: "pl",
    polonia: "pl",
    pl: "pl",
  };

  return countryCodes[value] || null;
};

const getPlaceCategoryLabel = (category, t) => {
  const safeCategory = category || "OTHER";

  return t(`categories.${safeCategory}`, {
    defaultValue: t("categories.OTHER", {
      defaultValue: "Outro",
    }),
  });
};

const getPlaceCategoryTone = (category) => {
  switch (category) {
    case "WAREHOUSE":
      return "places-category-badge--warehouse";
    case "INDUSTRY":
      return "places-category-badge--industry";
    case "GAS_STATION":
      return "places-category-badge--gas";
    case "PORT":
      return "places-category-badge--port";
    case "AIRPORT":
      return "places-category-badge--airport";
    case "CLIENT":
      return "places-category-badge--client";
    case "SUPPLIER":
      return "places-category-badge--supplier";
    case "PARKING":
      return "places-category-badge--parking";
    case "REST_AREA":
      return "places-category-badge--rest";
    case "CUSTOMS":
      return "places-category-badge--customs";
    case "WORKSHOP":
      return "places-category-badge--workshop";
    case "CONSTRUCTION_SITE":
      return "places-category-badge--construction";
    case "OTHER":
    default:
      return "places-category-badge--other";
  }
};

const placesFilterSelectStyles = {
  container: (base) => ({
    ...base,
    width: "100%",
    minWidth: 0,
  }),

  control: (
    base,
    state
  ) => ({
    ...base,

    minHeight: 38,
    height: 38,

    background:
      "#0f1318",

    border:
      state.isFocused
        ? "1px solid #4f5dff"
        : "1px solid #263244",

    borderRadius: 10,

    boxShadow:
      state.isFocused
        ? "0 0 0 2px rgba(79,93,255,0.15)"
        : "none",

    cursor: "pointer",

    "&:hover": {
      borderColor:
        state.isFocused
          ? "#4f5dff"
          : "#34445c",
    },
  }),

  valueContainer: (
    base
  ) => ({
    ...base,
    padding:
      "0 8px 0 34px",
  }),

  singleValue: (
    base
  ) => ({
    ...base,
    color:
      "#e5e7eb",
    fontSize: 12.5,
  }),

  placeholder: (
    base
  ) => ({
    ...base,
    color:
      "#8a96a8",
    fontSize: 12.5,
  }),

  input: (base) => ({
    ...base,
    color:
      "#e5e7eb",
    margin: 0,
    padding: 0,
  }),

  indicatorsContainer: (
    base
  ) => ({
    ...base,
    height: 38,
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),

  dropdownIndicator: (
    base
  ) => ({
    ...base,
    color:
      "#8c98aa",
    padding: 7,

    "&:hover": {
      color:
        "#ffffff",
    },
  }),

  clearIndicator: (
    base
  ) => ({
    ...base,
    color:
      "#8c98aa",
    padding: 6,

    "&:hover": {
      color:
        "#ffffff",
    },
  }),

  menuPortal: (
    base
  ) => ({
    ...base,
    zIndex: 99999,
  }),

  menu: (base) => ({
    ...base,

    marginTop: 6,

    background:
      "#111820",

    border:
      "1px solid #273548",

    borderRadius: 11,

    overflow:
      "hidden",

    boxShadow:
      "0 16px 38px rgba(0,0,0,0.48)",
  }),

  menuList: (
    base
  ) => ({
    ...base,
    padding: 5,
    maxHeight: 240,
  }),

  option: (
    base,
    state
  ) => ({
    ...base,

    borderRadius: 7,

    padding:
      "8px 10px",

    fontSize: 12.5,

    cursor: "pointer",

    color:
      state.isSelected
        ? "#ffffff"
        : "#d8e0eb",

    backgroundColor:
      state.isSelected
        ? "rgba(73,83,255,0.24)"
        : state.isFocused
        ? "rgba(73,83,255,0.12)"
        : "transparent",

    "&:active": {
      backgroundColor:
        "rgba(73,83,255,0.2)",
    },
  }),

  noOptionsMessage: (
    base
  ) => ({
    ...base,
    color:
      "#8d99aa",
    fontSize: 12.5,
  }),
};

function Places() {
  const { t } = useTranslation("places", { useSuspense: false });
  const navigate = useNavigate();

  useEffect(() => {
    const content =
      document.querySelector(
        ".content"
      );

    if (!content) {
      return undefined;
    }

    content.classList.add(
      "content--places"
    );

    return () => {
      content.classList.remove(
        "content--places"
      );
    };
  }, []);

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);

  const tableRef = useRef(null);
  const sentinelRef = useRef(null);

  const persistPlaces = useCallback((nextItems) => {
    try {
      window.localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          ts: Date.now(),
          items: nextItems,
          meta: {},
        })
      );
    } catch {}
  }, []);

  const {
    items: places,
    setItems: setPlaces,
    loading: isLoading,
    error: loadError,
    refresh: fetchPlaces,
  } = useInstantListCache({
    cacheKey: CACHE_KEY,
    storage: "local",
    fetcher: async ({ signal }) => {
      const { data } = await axios.get("/places", { signal });

      return {
        items: Array.isArray(data) ? data : [],
      };
    },
  });

  const setPlacesAndPersist = useCallback(
    (updater) => {
      setPlaces((prev) => {
        const next =
          typeof updater === "function"
            ? updater(prev)
            : updater;

        persistPlaces(next);
        return next;
      });
    },
    [persistPlaces, setPlaces]
  );

  useEffect(() => {
    if (!loadError) return;

    setErrorMessage(
      t("errors.load_failed", {
        defaultValue:
          "Falha ao carregar locais. Tente novamente mais tarde.",
      })
    );
  }, [loadError, t]);

  useEffect(() => {
    const socket = getSocket();

    const subscribe = () => {
      socket.emit("subscribe:places");
    };

    subscribe();
    socket.on("connect", subscribe);
    socket.on("reconnect", subscribe);

    const onCreated = (place) => {
      setPlacesAndPersist((prev) =>
        prev.some((item) => String(item.id) === String(place.id))
          ? prev
          : [place, ...prev]
      );
    };

    const onUpdated = (place) => {
      setPlacesAndPersist((prev) =>
        prev.map((item) =>
          String(item.id) === String(place.id)
            ? { ...item, ...place }
            : item
        )
      );
    };

    const onDeleted = ({ id }) => {
      setPlacesAndPersist((prev) =>
        prev.filter((item) => String(item.id) !== String(id))
      );
    };

    socket.on("place:created", onCreated);
    socket.on("place:updated", onUpdated);
    socket.on("place:deleted", onDeleted);

    return () => {
      socket.emit("unsubscribe:places");
      socket.off("connect", subscribe);
      socket.off("reconnect", subscribe);
      socket.off("place:created", onCreated);
      socket.off("place:updated", onUpdated);
      socket.off("place:deleted", onDeleted);
    };
  }, [setPlacesAndPersist]);

  const handleRefresh = useCallback(async () => {
    setErrorMessage("");

    try {
      await fetchPlaces();
    } catch {}
  }, [fetchPlaces]);

  const handleRemove = useCallback(
    async (id) => {
      const ok = window.confirm(
        t("confirm.remove_prompt", {
          defaultValue:
            "Tem a certeza de que pretende remover este local?",
        })
      );

      if (!ok) return;

      try {
        await axios.delete(`/places/${id}`);
        setErrorMessage("");
        setPlacesAndPersist((prev) =>
          prev.filter((place) => place.id !== id)
        );
      } catch {
        setErrorMessage(
          t("errors.delete_failed", {
            defaultValue:
              "Falha ao eliminar o local. Tente novamente mais tarde.",
          })
        );
      }
    },
    [setPlacesAndPersist, t]
  );

  const handleEditPlace = useCallback(
    (place) => {
      navigate(`/places/${place.id}/edit`, {
        state: { prefill: place },
      });
    },
    [navigate]
  );

  const categoryOptions = useMemo(() => {
    const unique = Array.from(
      new Set(
        places
          .map((place) => place.category)
          .filter(Boolean)
      )
    );

    return unique
      .map((category) => ({
        value: category,
        label: getPlaceCategoryLabel(category, t),
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, "pt", {
          sensitivity: "base",
        })
      );
  }, [places, t]);

  const countryOptions = useMemo(() => {
    const unique = Array.from(
      new Set(
        places
          .map((place) => place.country)
          .filter(Boolean)
      )
    );

    return unique
      .map((country) => ({
        value: country,
        label: country,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, "pt", {
          sensitivity: "base",
        })
      );
  }, [places]);

  const selectedCategoryFilter =
    categoryOptions.find(
      (option) =>
        option.value === categoryFilter
    ) || null;

  const selectedCountryFilter =
    countryOptions.find(
      (option) =>
        option.value === countryFilter
    ) || null;

  const menuPortalTarget =
    typeof document !== "undefined"
      ? document.body
      : null;

  const filteredPlaces = useMemo(() => {
    const query = normalizeText(searchTerm);

    return places.filter((place) => {
      const matchesSearch =
        !query ||
        [
          place.placeName,
          place.category,
          getPlaceCategoryLabel(place.category, t),
          place.address,
          place.city,
          place.country,
          place.postalCode,
          place.contactPhone,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .includes(query);

      const matchesCategory =
        !categoryFilter || place.category === categoryFilter;

      const matchesCountry =
        !countryFilter || place.country === countryFilter;

      return (
        matchesSearch &&
        matchesCategory &&
        matchesCountry
      );
    });
  }, [places, searchTerm, categoryFilter, countryFilter, t]);

  const hasActiveFilters =
    searchTerm.trim() ||
    categoryFilter.trim() ||
    countryFilter.trim();

  const totalCount = places.length;
  const visibleCount = filteredPlaces.length;

  const renderedCount = Math.min(visibleLimit, visibleCount);

  const visiblePlaces = useMemo(() => {
    return filteredPlaces.slice(0, visibleLimit);
  }, [filteredPlaces, visibleLimit]);

  useEffect(() => {
    setVisibleLimit(PAGE_SIZE);
  }, [searchTerm, categoryFilter, countryFilter]);

  useEffect(() => {
    setVisibleLimit((prev) =>
      Math.min(
        Math.max(prev, PAGE_SIZE),
        visibleCount || PAGE_SIZE
      )
    );
  }, [visibleCount]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (visibleLimit >= visibleCount) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleLimit((prev) =>
            Math.min(prev + PAGE_SIZE, visibleCount)
          );
        }
      },
      {
        root: null,
        rootMargin: "220px 0px",
        threshold: 0.01,
      }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [visibleLimit, visibleCount]);

  return (
    <div className="places-page">
      <section className="places-header-card">
        <div className="places-header">
          <div className="places-header__content">
            <h1 className="places-header__title">
              {t("header.title", {
                defaultValue: "Locais",
              })}
            </h1>

            <p className="places-header__subtitle">
              <strong>
                {totalCount}
              </strong>{" "}
              {t("header.registered", {
                defaultValue:
                  "locais registados",
              })}
            </p>
          </div>

          <div className="places-header__controls">
            <div className="places-search">
              <FaSearch className="places-search__icon" />

              <input
                className="places-search__input"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("search.placeholder", {
                  defaultValue:
                    "Procurar por nome, morada ou contacto...",
                })}
                aria-label={t("search.aria_label", {
                  defaultValue: "Procurar locais",
                })}
              />

              {searchTerm && (
                <button
                  type="button"
                  className="places-search__clear"
                  onClick={() => setSearchTerm("")}
                  aria-label={t("search.clear_aria", {
                    defaultValue: "Limpar pesquisa",
                  })}
                  title={t("search.clear_title", {
                    defaultValue: "Limpar",
                  })}
                >
                  ✕
                </button>
              )}
            </div>

            <div className="places-filter">
              <FaFilter className="places-filter__icon" />

              <Select
                value={selectedCategoryFilter}
                options={categoryOptions}
                onChange={(option) =>
                  setCategoryFilter(
                    option?.value || ""
                  )
                }
                placeholder={t("filters.category", {
                  defaultValue: "Categoria",
                })}
                aria-label={t("filters.category", {
                  defaultValue: "Categoria",
                })}
                styles={placesFilterSelectStyles}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                className="places-filter__select-container"
                classNamePrefix="places-filter-select"
                isSearchable={false}
                isClearable
              />
            </div>

            <div className="places-filter places-filter--country">
              <FaGlobeEurope className="places-filter__icon" />

              <Select
                value={selectedCountryFilter}
                options={countryOptions}
                onChange={(option) =>
                  setCountryFilter(
                    option?.value || ""
                  )
                }
                placeholder={t("filters.country", {
                  defaultValue: "País",
                })}
                aria-label={t("filters.country", {
                  defaultValue: "País",
                })}
                styles={placesFilterSelectStyles}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                className="places-filter__select-container"
                classNamePrefix="places-filter-select"
                isSearchable={false}
                isClearable
              />
            </div>

            <button
              className="places-btn places-btn--icon"
              onClick={handleRefresh}
              title={t("actions.refresh", {
                defaultValue: "Recarregar",
              })}
              type="button"
            >
              <FaSyncAlt className="places-btn__icon" />
            </button>

            <button
              className="places-btn places-btn--primary"
              onClick={() => navigate("/places/create")}
              type="button"
            >
              <FaPlus className="places-btn__icon" />
              <span>
                {t("actions.new_place", {
                  defaultValue: "Adicionar local",
                })}
              </span>
            </button>
          </div>
        </div>
      </section>

      {errorMessage && (
        <section className="places-card places-error">
          <div className="places-error__text">
            {errorMessage}
          </div>

          <button
            className="places-btn"
            onClick={handleRefresh}
            type="button"
          >
            {t("errors.try_again", {
              defaultValue: "Tentar novamente",
            })}
          </button>
        </section>
      )}

      <section className="places-card places-table-card">
        <div className="places-table" ref={tableRef}>
          <div className="places-table__header">
            <div>{t("table.columns.place_name")}</div>
            <div>{t("table.columns.category")}</div>
            <div>{t("table.columns.contact")}</div>
            <div>{t("table.columns.address")}</div>
            <div>{t("table.columns.zip_code")}</div>
            <div>{t("table.columns.city")}</div>
            <div>{t("table.columns.country")}</div>
            <div className="places-col-actions">
              {t("table.columns.actions")}
            </div>
          </div>

          {isLoading && places.length === 0 ? (
            Array.from({ length: 6 }).map((_, index) => (
              <div
                className="places-row places-row--skeleton"
                key={`skeleton-${index}`}
              >
                <div className="places-cell">
                  <span className="places-skel" />
                </div>
                <div className="places-cell">
                  <span className="places-skel" />
                </div>
                <div className="places-cell">
                  <span className="places-skel" />
                </div>
                <div className="places-cell">
                  <span className="places-skel" />
                </div>
                <div className="places-cell">
                  <span className="places-skel" />
                </div>
                <div className="places-cell">
                  <span className="places-skel" />
                </div>
                <div className="places-cell">
                  <span className="places-skel" />
                </div>
                <div className="places-cell places-cell--actions">
                  <span className="places-skel places-skel--small" />
                </div>
              </div>
            ))
          ) : visibleCount > 0 ? (
            <>
              {visiblePlaces.map((place) => {
                const categoryLabel = getPlaceCategoryLabel(
                  place.category,
                  t
                );

                const categoryTone = getPlaceCategoryTone(
                  place.category
                );

                const countryCode = getCountryCode(
                  place.country
                );

                return (
                  <div
                    key={place.id}
                    className="places-row"
                    onClick={() => handleEditPlace(place)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" ||
                        e.key === " "
                      ) {
                        e.preventDefault();
                        handleEditPlace(place);
                      }
                    }}
                  >
                    <div
                      className="places-cell places-place-cell"
                      title={place.placeName || ""}
                    >
                      <span className="places-place-icon">
                        <LuBuilding2 />
                      </span>

                      <span className="places-place-name">
                        {place.placeName || "—"}
                      </span>
                    </div>

                    <div
                      className="places-cell"
                      title={categoryLabel}
                    >
                      <span
                        className={`places-category-badge ${categoryTone}`}
                      >
                        <LuTags className="places-category-badge__icon" />
                        <span className="places-category-badge__text">
                          {categoryLabel}
                        </span>
                      </span>
                    </div>

                    <div
                      className="places-cell places-cell--with-icon"
                      title={place.contactPhone || ""}
                    >
                      <LuPhone className="places-cell-icon" />
                      <span>{place.contactPhone || "—"}</span>
                    </div>

                    <div
                      className="places-cell places-cell--with-icon"
                      title={place.address || ""}
                    >
                      <LuBuilding2 className="places-cell-icon" />
                      <span>{place.address || "—"}</span>
                    </div>

                    <div
                      className="places-cell"
                      title={place.postalCode || ""}
                    >
                      {place.postalCode || "—"}
                    </div>

                    <div
                      className="places-cell places-cell--with-icon"
                      title={place.city || ""}
                    >
                      <LuMapPin className="places-cell-icon" />
                      <span>{place.city || "—"}</span>
                    </div>

                    <div
                      className="places-cell places-cell--with-icon"
                      title={place.country || ""}
                    >
                      {countryCode ? (
                        <span
                          className={`places-country-flag fi fi-${countryCode}`}
                          aria-hidden="true"
                        />
                      ) : (
                        <span
                          className="places-country-flag places-country-flag--fallback"
                          aria-hidden="true"
                        />
                      )}

                      <span>{place.country || "—"}</span>
                    </div>

                    <div
                      className="places-cell places-cell--actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="places-icon-btn"
                        title={t("table.remove.title")}
                        onClick={() => handleRemove(place.id)}
                        aria-label={t("table.remove.aria")}
                        type="button"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>
                );
              })}

              {renderedCount < visibleCount && (
                <div
                  ref={sentinelRef}
                  className="places-infinite-sentinel"
                />
              )}
            </>
          ) : (
            <div className="places-empty">
              <div className="places-empty__title">
                {t("empty.title")}
              </div>

              <div className="places-empty__subtitle">
                {t("empty.subtitle")}
              </div>

              <button
                className="places-btn places-btn--primary places-empty__button"
                onClick={() => navigate("/places/create")}
                type="button"
              >
                <FaPlus className="places-btn__icon" />
                {t("empty.add_place")}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Places;